import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { Add, Block, CheckCircle, ContentCopy, Publish, Save, Visibility } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { formularioAdminService } from '@/services/formularioAdminService'
import { produtoService } from '@/services/produtoService'
import { SecaoProduto } from '@/components/produtos/SecaoProduto'
import ItemFormularioEditor from '@/components/produtos/ItemFormularioEditor'
import PreviaFormularioProduto from '@/components/produtos/PreviaFormularioProduto'
import {
  ETAPA_FORMULARIO_LABEL,
  EtapaFormulario,
  FormulariosProdutoEtapa,
  SchemaFormulario,
  STATUS_FORMULARIO_LABEL,
  VersaoFormularioProduto,
} from '@/types/formularioAdmin'
import {
  mesmoSchema,
  normalizarSchema,
  novoItem,
  problemasDoSchema,
  schemaVazio,
} from '@/utils/formularioSchemaAdmin'

/**
 * Formulário do produto — dados solicitados ao consultor (AN-6 §6, L5).
 *
 * Um formulário por produto e etapa (Cadastro = Nova Reserva etapa 3; Estruturação =
 * "Dados do produto" do workspace). Cada publicação é uma **versão imutável**: a reserva
 * fica presa à versão vigente quando o vínculo é feito, então publicar a N+1 não cria
 * exigência nova para reserva já criada. Quem valida (estrutura e respostas) e quem decide
 * obrigatoriedade é o backend de oportunidades; esta tela edita o rascunho e publica.
 *
 * Não confundir com a aba Elegibilidade: aquelas perguntas são **regra de corte** avaliada
 * antes da reserva existir; estes campos são **dados** que a reserva precisa ter.
 *
 * **Dupla aprovação (V64, AN-PP6 §3):** "Enviar para aprovação" PROPÕE a versão N+1; ela só vale quando OUTRA
 * pessoa (gerente ou administrador, `podeAprovar`) aprova. Aprovar/Rejeitar ficam desabilitados para quem propôs
 * (o backend recusa com 403 `APROVACAO_PELO_PROPONENTE` de qualquer forma; a tela só explica).
 */

const TEXTO_PROPRIA_PROPOSTA =
  'Você enviou esta versão: a aprovação (ou rejeição) é de outra pessoa — gerente ou administrador.'

const ETAPAS: EtapaFormulario[] = ['CADASTRO', 'ESTRUTURACAO']

const DESCRICAO_ETAPA: Record<EtapaFormulario, string> = {
  CADASTRO: 'Aparece na Nova Reserva (etapa 3), acima das perguntas cadastrais. Fixado na criação da reserva.',
  ESTRUTURACAO:
    'Aparece na Estruturação, etapa "Dados do produto". Fixado quando a reserva entra em estruturação.',
}

const mensagemDe = (e: unknown, padrao: string) => {
  const err = e as { message?: string; data?: { message?: string } } | null
  return err?.data?.message || err?.message || padrao
}

const dataHora = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—')

const copia = (s: SchemaFormulario): SchemaFormulario => JSON.parse(JSON.stringify(s)) as SchemaFormulario

interface EtapaProps {
  produtoId: string
  etapa: EtapaFormulario
  podeEditar: boolean
  podeAprovar: boolean
  usuarioId: string | null
}

const EditorEtapa: React.FC<EtapaProps> = ({ produtoId, etapa, podeEditar, podeAprovar, usuarioId }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [dados, setDados] = useState<FormulariosProdutoEtapa | null>(null)
  const [schema, setSchema] = useState<SchemaFormulario>(schemaVazio())
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState<'salvando' | 'publicando' | null>(null)
  const [visao, setVisao] = useState<'editar' | 'previa'>('editar')
  const [confirmarPublicacao, setConfirmarPublicacao] = useState(false)
  const [duplicarAberto, setDuplicarAberto] = useState(false)
  const [versaoAberta, setVersaoAberta] = useState<VersaoFormularioProduto | null>(null)
  const [decisao, setDecisao] = useState<'aprovar' | 'rejeitar' | null>(null)
  const [motivoRejeicao, setMotivoRejeicao] = useState('')
  const [decidindo, setDecidindo] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const d = await formularioAdminService.doProduto(produtoId, etapa)
      setDados(d)
      setSchema(copia(d.rascunho?.schema ?? d.vigente?.schema ?? schemaVazio()))
    } catch (e) {
      setErro(mensagemDe(e, 'Não foi possível carregar o formulário do produto.'))
    } finally {
      setCarregando(false)
    }
  }, [produtoId, etapa])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const vigente = dados?.vigente ?? null
  const rascunho = dados?.rascunho ?? null
  const pendente = dados?.pendente ?? null
  const propriaProposta = !!pendente && !!usuarioId && pendente.propostoPor === usuarioId
  const salvo = rascunho?.schema ?? vigente?.schema ?? schemaVazio()
  const alterado = !mesmoSchema(schema, salvo)
  const problemas = useMemo(() => problemasDoSchema(schema, etapa), [schema, etapa])
  // Toda versão numerada conta (a pendente e a rejeitada reservam o número delas — V64).
  const versaoAtual = Math.max(
    vigente?.versao ?? 0,
    pendente?.versao ?? 0,
    ...(dados?.historico ?? []).filter((h) => h.status !== 'RASCUNHO').map((h) => h.versao)
  )
  const proxima = versaoAtual + 1
  // Publicar exige algo diferente do vigente: um rascunho salvo ou alteração na tela.
  const temOQuePublicar =
    alterado || (!!rascunho && !mesmoSchema(rascunho.schema ?? schemaVazio(), vigente?.schema ?? schemaVazio()))
  const somenteLeitura = !podeEditar

  const salvarRascunho = async (silencioso = false) => {
    const r = await formularioAdminService.salvarRascunho(produtoId, etapa, normalizarSchema(schema))
    if (!silencioso) enqueueSnackbar('Rascunho salvo. Ainda não vale para nenhuma reserva.', { variant: 'success' })
    return r
  }

  const onSalvar = async () => {
    setOcupado('salvando')
    try {
      await salvarRascunho()
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemDe(e, 'Falha ao salvar o rascunho.'), { variant: 'error' })
    } finally {
      setOcupado(null)
    }
  }

  const onPublicar = async () => {
    setOcupado('publicando')
    try {
      if (alterado || !rascunho) await salvarRascunho(true)
      const v = await formularioAdminService.publicar(produtoId, etapa)
      setConfirmarPublicacao(false)
      enqueueSnackbar(
        `Versão ${v?.versao ?? proxima} enviada para aprovação. Vale depois que outra pessoa (gerente ou administrador) aprovar.`,
        { variant: 'success' }
      )
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemDe(e, 'Falha ao enviar o formulário para aprovação.'), { variant: 'error' })
    } finally {
      setOcupado(null)
    }
  }

  const decidir = async () => {
    if (!pendente || !decisao) return
    setDecidindo(true)
    try {
      if (decisao === 'aprovar') {
        // Revisão PP6-valr (A1): aprova ESTA proposta (id) com o schema que a tela mostrou (hash).
        const v = await formularioAdminService.aprovar(produtoId, etapa, pendente.id, {
          versaoRegistro: pendente.versaoRegistro ?? null,
          schemaHash: pendente.schemaHash ?? null,
        })
        enqueueSnackbar(`Versão ${v?.versao ?? pendente.versao} aprovada e publicada.`, { variant: 'success' })
      } else {
        await formularioAdminService.rejeitar(produtoId, etapa, pendente.id, {
          motivo: motivoRejeicao.trim(),
          versaoRegistro: pendente.versaoRegistro ?? null,
          schemaHash: pendente.schemaHash ?? null,
        })
        enqueueSnackbar(`Versão ${pendente.versao} rejeitada.`, { variant: 'success' })
      }
      setDecisao(null)
      setMotivoRejeicao('')
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemDe(e, decisao === 'aprovar' ? 'Falha ao aprovar.' : 'Falha ao rejeitar.'), {
        variant: 'error',
      })
      setDecisao(null)
    } finally {
      setDecidindo(false)
    }
  }

  const abrirVersao = async (v: VersaoFormularioProduto) => {
    if (v.schema) {
      setVersaoAberta(v)
      return
    }
    try {
      setVersaoAberta(await formularioAdminService.versao(produtoId, v.id))
    } catch (e) {
      enqueueSnackbar(mensagemDe(e, 'Não foi possível abrir a versão.'), { variant: 'error' })
    }
  }

  if (carregando) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label={`Carregando formulário de ${ETAPA_FORMULARIO_LABEL[etapa]}`} />
      </Box>
    )
  }

  if (erro) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void carregar()}>
            Tentar de novo
          </Button>
        }
      >
        {erro}
      </Alert>
    )
  }

  const itens = schema.itens
  const setItens = (novos: typeof itens) => setSchema({ itens: novos })
  const historico = (dados?.historico ?? []).filter((h) => h.status !== 'RASCUNHO')

  return (
    <Stack spacing={3}>
      {pendente && (
        <Alert
          severity="warning"
          aria-label={`Proposta da versão ${pendente.versao}`}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button color="inherit" size="small" startIcon={<Visibility />} onClick={() => void abrirVersao(pendente)}>
                Ver
              </Button>
              {podeAprovar && (
                <>
                  <Tooltip title={propriaProposta ? TEXTO_PROPRIA_PROPOSTA : ''}>
                    <span>
                      <Button
                        color="error"
                        size="small"
                        startIcon={<Block />}
                        onClick={() => setDecisao('rejeitar')}
                        disabled={decidindo || propriaProposta}
                      >
                        Rejeitar
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={propriaProposta ? TEXTO_PROPRIA_PROPOSTA : ''}>
                    <span>
                      <Button
                        color="success"
                        size="small"
                        variant="contained"
                        startIcon={<CheckCircle />}
                        onClick={() => setDecisao('aprovar')}
                        disabled={decidindo || propriaProposta}
                      >
                        Aprovar versão {pendente.versao}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
            </Stack>
          }
        >
          Versão {pendente.versao} aguardando aprovação
          {propriaProposta ? ' (enviada por você)' : ''}: ainda não vale para nenhuma reserva — passa a valer quando
          outra pessoa (gerente ou administrador) aprovar.
        </Alert>
      )}
      <SecaoProduto
        titulo={`Formulário de ${ETAPA_FORMULARIO_LABEL[etapa]}`}
        descricao={DESCRICAO_ETAPA[etapa]}
        acao={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={visao}
            onChange={(_, v) => v && setVisao(v)}
            aria-label="Modo de visualização"
          >
            <ToggleButton value="editar">{somenteLeitura ? 'Campos' : 'Editar'}</ToggleButton>
            <ToggleButton value="previa">Prévia</ToggleButton>
          </ToggleButtonGroup>
        }
        rodape={
          podeEditar ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Save />}
                onClick={() => void onSalvar()}
                disabled={!!ocupado || !alterado || problemas.length > 0}
              >
                {ocupado === 'salvando' ? 'Salvando…' : 'Salvar rascunho'}
              </Button>
              <Tooltip title={pendente ? 'Já há uma versão aguardando aprovação: aprove ou rejeite antes de enviar outra' : ''}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Publish />}
                    onClick={() => setConfirmarPublicacao(true)}
                    disabled={!!ocupado || !temOQuePublicar || problemas.length > 0 || !!pendente}
                  >
                    Enviar versão {proxima} para aprovação
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          ) : undefined
        }
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {vigente ? (
            <Chip
              color="success"
              size="small"
              label={`Vigente: versão ${vigente.versao} · publicada em ${dataHora(vigente.publicadoEm)}`}
            />
          ) : (
            <Chip size="small" label="Nenhuma versão publicada" />
          )}
          {rascunho && <Chip color="warning" size="small" variant="outlined" label="Há rascunho não publicado" />}
          {pendente && (
            <Chip color="warning" size="small" label={`Versão ${pendente.versao} aguardando aprovação`} />
          )}
          {alterado && <Chip color="warning" size="small" label="Alterações não salvas" />}
        </Stack>

        {somenteLeitura && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Somente leitura: só o ADMIN edita e publica o formulário do produto.
          </Alert>
        )}

        {podeEditar && problemas.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>Corrija antes de salvar ou publicar:</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {problemas.map((p) => (
                <li key={p}>
                  <Typography variant="body2">{p}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {visao === 'previa' ? (
          <PreviaFormularioProduto schema={normalizarSchema(schema)} />
        ) : itens.length === 0 ? (
          <Alert severity="info">
            Nenhum campo — este produto não solicita dados nesta etapa.
            {podeEditar && vigente && ' Publicar sem campos encerra o formulário daqui em diante.'}
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {itens.map((item, i) => (
              <ItemFormularioEditor
                key={i}
                item={item}
                itensTopo={itens}
                indiceTopo={i}
                total={itens.length}
                etapa={etapa}
                somenteLeitura={somenteLeitura}
                onChange={(novo) => setItens(itens.map((x, y) => (y === i ? novo : x)))}
                onRemover={() => setItens(itens.filter((_, y) => y !== i))}
                onMover={(d) => {
                  const lista = [...itens]
                  const [m] = lista.splice(i, 1)
                  lista.splice(i + d, 0, m)
                  setItens(lista)
                }}
              />
            ))}
          </Stack>
        )}

        {podeEditar && visao === 'editar' && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Button startIcon={<Add />} onClick={() => setItens([...itens, novoItem()])}>
              Campo
            </Button>
            <Button startIcon={<Add />} onClick={() => setItens([...itens, novoItem('GRUPO')])}>
              Grupo repetível
            </Button>
            <Button startIcon={<ContentCopy />} onClick={() => setDuplicarAberto(true)}>
              Duplicar formulário de outro produto
            </Button>
          </Stack>
        )}
      </SecaoProduto>

      <SecaoProduto
        titulo="Histórico de versões"
        descricao="Versões publicadas não mudam. Cada reserva continua na versão a que foi vinculada."
      >
        {historico.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nenhuma versão publicada ainda.</Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label={`Histórico do formulário de ${ETAPA_FORMULARIO_LABEL[etapa]}`}>
              <TableHead>
                <TableRow>
                  <TableCell>Versão</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell>Publicada em</TableCell>
                  <TableCell>Hash</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {[...historico]
                  .sort((a, b) => b.versao - a.versao)
                  .map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>{h.versao}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={h.status === 'PUBLICADA' ? 'success' : 'default'}
                          label={STATUS_FORMULARIO_LABEL[h.status]}
                        />
                      </TableCell>
                      <TableCell>{dataHora(h.publicadoEm)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{h.schemaHash ? h.schemaHash.slice(0, 8) : '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<Visibility />} onClick={() => void abrirVersao(h)}>
                          Ver versão {h.versao}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SecaoProduto>

      <Dialog open={confirmarPublicacao} onClose={() => !ocupado && setConfirmarPublicacao(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar versão {proxima} para aprovação</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Alert severity="info">
              A versão {proxima} só passa a valer quando outra pessoa (gerente ou administrador) aprovar.
              {vigente
                ? ` Reservas já criadas continuam na versão ${vigente.versao}; depois da aprovação, a versão ${proxima} vale para as reservas vinculadas.`
                : ''}
            </Alert>
            {itens.length === 0 && (
              <Alert severity="warning">
                Sem campos: a partir desta versão o produto deixa de solicitar dados na etapa{' '}
                {ETAPA_FORMULARIO_LABEL[etapa]}.
              </Alert>
            )}
            <Typography variant="body2">Uma versão publicada não pode ser editada — mudanças viram a versão seguinte.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarPublicacao(false)} disabled={!!ocupado}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void onPublicar()} disabled={!!ocupado}>
            {ocupado === 'publicando' ? 'Enviando…' : `Confirmar envio da versão ${proxima} para aprovação`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={decisao !== null} onClose={() => !decidindo && setDecisao(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {decisao === 'aprovar' ? `Aprovar versão ${pendente?.versao}` : `Rejeitar versão ${pendente?.versao}`}
        </DialogTitle>
        <DialogContent>
          {decisao === 'aprovar' ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              A versão {pendente?.versao} passa a valer agora para as reservas vinculadas daqui em diante
              {vigente ? `; a versão ${vigente.versao} vira histórico e as reservas já criadas continuam nela` : ''}.
            </Alert>
          ) : (
            <TextField
              label="Motivo da rejeição"
              value={motivoRejeicao}
              onChange={(e) => setMotivoRejeicao(e.target.value)}
              multiline
              minRows={2}
              required
              inputProps={{ maxLength: 500 }}
              fullWidth
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisao(null)} disabled={decidindo}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={decisao === 'aprovar' ? 'success' : 'error'}
            onClick={() => void decidir()}
            disabled={decidindo || (decisao === 'rejeitar' && !motivoRejeicao.trim())}
          >
            {decisao === 'aprovar' ? 'Aprovar' : 'Rejeitar'}
          </Button>
        </DialogActions>
      </Dialog>

      {duplicarAberto && (
        <DuplicarDialog
          produtoId={produtoId}
          etapa={etapa}
          onFechar={() => setDuplicarAberto(false)}
          onCopiar={(s) => {
            setSchema(copia(s))
            setDuplicarAberto(false)
            setVisao('editar')
            enqueueSnackbar('Campos copiados. Revise e salve o rascunho.', { variant: 'info' })
          }}
        />
      )}

      <Dialog open={!!versaoAberta} onClose={() => setVersaoAberta(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Versão {versaoAberta?.versao} · {versaoAberta ? STATUS_FORMULARIO_LABEL[versaoAberta.status] : ''}
        </DialogTitle>
        <DialogContent>
          {versaoAberta && <PreviaFormularioProduto schema={versaoAberta.schema ?? schemaVazio()} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersaoAberta(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

interface DuplicarProps {
  produtoId: string
  etapa: EtapaFormulario
  onFechar: () => void
  onCopiar: (schema: SchemaFormulario) => void
}

/** "Duplicar formulário de outro produto" (§5): copia a versão vigente (ou o rascunho) para a tela. */
const DuplicarDialog: React.FC<DuplicarProps> = ({ produtoId, etapa, onFechar, onCopiar }) => {
  const [produtos, setProdutos] = useState<{ id: string; nome: string }[] | null>(null)
  const [origem, setOrigem] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [copiando, setCopiando] = useState(false)

  useEffect(() => {
    let ativo = true
    produtoService
      .listar(0, 200)
      .then((p) => {
        if (ativo) setProdutos((p?.content ?? []).filter((x) => x.id !== produtoId).map((x) => ({ id: x.id, nome: x.nome })))
      })
      .catch(() => ativo && setErro('Não foi possível listar os produtos.'))
    return () => {
      ativo = false
    }
  }, [produtoId])

  const copiar = async () => {
    setCopiando(true)
    setErro(null)
    try {
      const d = await formularioAdminService.doProduto(origem, etapa)
      const s = d.vigente?.schema ?? d.rascunho?.schema
      if (!s || s.itens.length === 0) {
        setErro(`Esse produto não tem formulário de ${ETAPA_FORMULARIO_LABEL[etapa]}.`)
        return
      }
      onCopiar(s)
    } catch (e) {
      setErro(mensagemDe(e, 'Não foi possível ler o formulário do outro produto.'))
    } finally {
      setCopiando(false)
    }
  }

  return (
    <Dialog open onClose={onFechar} maxWidth="sm" fullWidth>
      <DialogTitle>Duplicar formulário de outro produto</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            Os campos de {ETAPA_FORMULARIO_LABEL[etapa]} do produto escolhido substituem os da tela. Nada é gravado até
            você salvar o rascunho.
          </Typography>
          {produtos === null && !erro ? (
            <CircularProgress size={24} aria-label="Carregando produtos" />
          ) : (
            <TextField
              select
              label="Produto de origem"
              value={origem}
              onChange={(e) => setOrigem(e.target.value)}
              size="small"
              fullWidth
            >
              {(produtos ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
              ))}
            </TextField>
          )}
          {erro && <Alert severity="error">{erro}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button variant="contained" disabled={!origem || copiando} onClick={() => void copiar()}>
          {copiando ? 'Copiando…' : 'Copiar campos'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface Props {
  produtoId: string
  /** ADMIN: edita e envia para aprovação. Demais perfis do back-office: só leitura (o GET é OPERADOR+). */
  podeEditar: boolean
  /** V64: GERENTE ou ADMIN — aprova/rejeita a proposta pendente de OUTRA pessoa. */
  podeAprovar?: boolean
  /** `sub` do usuário logado: quem propôs não decide a própria proposta. */
  usuarioId?: string | null
}

const FormularioProdutoEditor: React.FC<Props> = ({ produtoId, podeEditar, podeAprovar = false, usuarioId = null }) => {
  const [etapa, setEtapa] = useState<EtapaFormulario>('CADASTRO')
  // Etapa visitada fica montada (escondida) para não perder alteração ao trocar de sub-aba.
  const [visitadas, setVisitadas] = useState<EtapaFormulario[]>(['CADASTRO'])

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        Dados que a reserva deste produto precisa ter. Diferente da Elegibilidade, que é regra de corte avaliada antes da
        reserva existir.
      </Alert>
      <Tabs
        value={etapa}
        onChange={(_, v: EtapaFormulario) => {
          setEtapa(v)
          setVisitadas((prev) => (prev.includes(v) ? prev : [...prev, v]))
        }}
        aria-label="Etapa do formulário"
      >
        {ETAPAS.map((e) => (
          <Tab key={e} value={e} label={ETAPA_FORMULARIO_LABEL[e]} />
        ))}
      </Tabs>
      {ETAPAS.filter((e) => visitadas.includes(e)).map((e) => (
        <Box key={e} hidden={e !== etapa}>
          <EditorEtapa
            produtoId={produtoId}
            etapa={e}
            podeEditar={podeEditar}
            podeAprovar={podeAprovar}
            usuarioId={usuarioId}
          />
        </Box>
      ))}
    </Stack>
  )
}

export default FormularioProdutoEditor
